import { useEffect, useMemo, useRef, useState } from "react";

import { getMcapRankedSchools } from "api/api.service";
import typography from "../typography";

/**
 * School view — the control that scopes the whole dashboard to one school.
 *
 * WHY THIS REPLACED A <select>
 * The dropdown listed every school alphabetically, which put "#DEMO Ease
 * Sandbox" at the top and buried the schools an administrator actually opens
 * this dashboard to look at. Ranked by resets in the selected window instead,
 * so the first thing offered is the school doing the most right now — and
 * typing filters the whole list, including the quiet schools, because finding
 * a school that has gone silent is exactly why someone opens this.
 *
 * The bar beside each name is the point: a picker for an analytics dashboard
 * should show the data it is a picker for.
 */
export default function SchoolPicker({ value, onChange, filters }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Ranked over the SAME window the dashboard is showing — "most active" has
  // to mean active in the period being looked at, or the ranking quietly
  // describes a different question than the one on screen.
  const rangeKey = `${filters?.startDate || ""}:${filters?.endDate || ""}`;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getMcapRankedSchools({ ...(filters?.startDate ? { startDate: filters.startDate } : {}), ...(filters?.endDate ? { endDate: filters.endDate } : {}) }, controller.signal)
      .then((res) => setSchools(res?.schools ?? []))
      .catch(() => setSchools([]))
      .finally(() => setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  useEffect(() => {
    if (!open) return undefined;
    // Focus the field on open so the admin can type immediately — the whole
    // point of replacing the dropdown was to make searching the default.
    inputRef.current?.focus();
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = useMemo(() => schools.find((s) => String(s.schoolId) === String(value)) ?? null, [schools, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter((s) => s.name.toLowerCase().includes(q) || s.districtName.toLowerCase().includes(q));
  }, [schools, query]);

  const maxResets = useMemo(() => filtered.reduce((m, s) => Math.max(m, s.resets), 0) || 1, [filtered]);

  const choose = (schoolId) => {
    onChange(schoolId == null ? "" : String(schoolId));
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="mcap-schoolpicker" ref={rootRef}>
      <span className="mcap-filter-label">{typography.SCHOOL_VIEW}</span>

      <button type="button" className="mcap-schoolpicker-trigger" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <i className="mdi mdi-magnify" aria-hidden="true" />
        <span className={selected ? "mcap-schoolpicker-value" : "mcap-schoolpicker-placeholder"}>
          {selected ? selected.name : typography.ALL_SCHOOLS}
        </span>
        <i className="mdi mdi-chevron-down mcap-schoolpicker-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="mcap-schoolpicker-panel" role="listbox">
          <div className="mcap-schoolpicker-search">
            <i className="mdi mdi-magnify" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={typography.SCHOOL_SEARCH_PLACEHOLDER}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button type="button" className="mcap-schoolpicker-clear" onClick={() => setQuery("")} aria-label="Clear search">
                <i className="mdi mdi-close" />
              </button>
            )}
          </div>

          <button type="button" className={`mcap-schoolpicker-all${!value ? " is-selected" : ""}`} onClick={() => choose(null)}>
            <span>{typography.ALL_SCHOOLS}</span>
            <span className="mcap-schoolpicker-allhint">{typography.SCHOOL_ALL_HINT}</span>
          </button>

          <p className="mcap-schoolpicker-heading">{query ? typography.SCHOOL_MATCHES : typography.SCHOOL_MOST_ACTIVE}</p>

          <ul className="mcap-schoolpicker-list">
            {loading && <li className="mcap-schoolpicker-state">Loading…</li>}
            {!loading && filtered.length === 0 && <li className="mcap-schoolpicker-state">{typography.SCHOOL_NO_MATCH}</li>}
            {!loading &&
              filtered.map((school) => (
                <li key={school.schoolId}>
                  <button
                    type="button"
                    className={`mcap-schoolpicker-item${String(school.schoolId) === String(value) ? " is-selected" : ""}`}
                    onClick={() => choose(school.schoolId)}
                    role="option"
                    aria-selected={String(school.schoolId) === String(value)}
                  >
                    <span className="mcap-schoolpicker-name">
                      {school.name}
                      {school.districtName && <em>{school.districtName}</em>}
                    </span>
                    <span className="mcap-schoolpicker-bar" aria-hidden="true">
                      <span style={{ width: `${(school.resets / maxResets) * 100}%` }} />
                    </span>
                    <span className="mcap-schoolpicker-count">
                      {school.resets}
                      {/* Zero is the useful signal here, not a gap — it is how
                          an administrator spots a school that has stopped. */}
                      <em>{school.resets === 1 ? "reset" : "resets"}</em>
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
